import { Rng } from '../core/rng';
import { TAU } from '../core/math';
import { SHIP_CLASSES, type DifficultyId, type ShipClassId } from '../data/classes';
import { Vessel, type Faction } from './vessel';
import { Captain } from './captain';

// Who else is out on the water. Groups are spawned beyond the horizon with
// their own business - patrols, convoys, hunting pairs - and routes that may
// or may not bring them across the player's path. Nothing is scripted after
// that: whether there is a fight is up to their lookouts and the player.

export type GroupKind = 'lone_raider' | 'hunters' | 'convoy' | 'squadron' | 'rival';

interface Group {
  kind: GroupKind;
  vessels: Vessel[];
}

const NAMES: Record<Faction, string[]> = {
  navy: ['HMS Vigilant', 'HMS Retribution', 'HMS Kestrel', 'HMS Dauntless', 'HMS Tern', 'HMS Resolute', 'HMS Argus', 'HMS Swift'],
  pirate: ['Black Maria', 'Revenant', 'Sea Wolf', 'Crimson Gull', 'Widowmaker', "Devil's Due", 'Salt Hag', 'Scourge'],
  merchant: ['Providence', 'Good Intent', 'Mary Ellen', 'Two Brothers', 'Fortune', 'Hopewell', 'Charming Nancy', 'Industry'],
  player: ['Tidebreaker'],
};

/** A group this far from our ship (m), and out of the fight, is dropped. */
export const DROP_RANGE = 12000;

export class Encounters {
  enabled = true;
  difficulty: DifficultyId = 'normal';
  readonly groups: Group[] = [];
  private timer = 45;
  private readonly rng: Rng;
  private names = new Map<Faction, number>();

  constructor(seed: number) {
    this.rng = new Rng(seed);
  }

  /** The next name for a ship of `f` (the dev tools name theirs this way too). */
  name(f: Faction): string {
    const i = this.names.get(f) ?? this.rng.int(8);
    this.names.set(f, i + 1);
    return NAMES[f][i % NAMES[f].length]!;
  }

  /** The ships that would be dropped were our ship at (x, z) (the dev chart warns of them). */
  droppedFrom(x: number, z: number): Vessel[] {
    const out: Vessel[] = [];
    for (const g of this.groups) {
      const live = g.vessels.filter((v) => v.state !== 'sunk');
      if (live.length && live.every((v) => Math.hypot(v.ship.com.x - x, v.ship.com.z - z) > DROP_RANGE && !v.battle)) out.push(...live);
    }
    return out;
  }

  /** Spawn, despawn. Returns vessels to add / remove this step. */
  update(dt: number, player: Vessel, all: Vessel[]): { add: Vessel[]; remove: Vessel[] } {
    const add: Vessel[] = [], remove: Vessel[] = [];
    // Groups far away and out of the fight fade from the chart.
    for (let i = this.groups.length - 1; i >= 0; i--) {
      const g = this.groups[i]!;
      const live = g.vessels.filter((v) => v.state !== 'sunk');
      const far = live.every((v) => Math.hypot(v.ship.com.x - player.ship.com.x, v.ship.com.z - player.ship.com.z) > DROP_RANGE && !v.battle);
      if (live.length === 0 || far) {
        remove.push(...g.vessels);
        this.groups.splice(i, 1);
      }
    }
    // Switched off: nothing new comes over the horizon; those already out
    // here sail on until they're out of sight.
    if (!this.enabled) return { add, remove };
    this.timer -= dt;
    const active = all.filter((v) => v !== player && v.alive).length;
    if (this.timer <= 0 && this.groups.length < 2 && active < 5) {
      this.timer = this.rng.range(120, 260);
      add.push(...this.spawn(this.pick(), player));
    }
    return { add, remove };
  }

  private pick(): GroupKind {
    const hard = this.difficulty === 'hard' || this.difficulty === 'legendary';
    const table: [GroupKind, number][] = [
      ['lone_raider', 3], ['hunters', 3], ['convoy', 3], ['rival', 2], ['squadron', hard ? 2 : 0.6],
    ];
    const total = table.reduce((a, [, w]) => a + w, 0);
    let r = this.rng.range(0, total);
    for (const [k, w] of table) {
      r -= w;
      if (r <= 0) return k;
    }
    return 'lone_raider';
  }

  spawn(kind: GroupKind, player: Vessel, distance = this.rng.range(4200, 6500)): Vessel[] {
    const p = player.ship.com;
    // Somewhere over the horizon.
    const a = this.rng.range(0, TAU);
    const x = p.x + Math.cos(a) * distance;
    const z = p.z + Math.sin(a) * distance;
    const comp: [ShipClassId, Faction][] =
      kind === 'lone_raider' ? [['raider', 'pirate']]
      : kind === 'hunters' ? (this.rng.chance(0.5) ? [['frigate', 'navy'], ['schooner', 'navy']] : [['schooner', 'pirate'], ['raider', 'pirate']])
      : kind === 'convoy' ? [['merchant', 'merchant'], ['merchant', 'merchant'], ...(this.rng.chance(0.4) ? [['merchant', 'merchant'] as [ShipClassId, Faction]] : []), ...(this.difficulty !== 'easy' || this.rng.chance(0.5) ? [['frigate', 'navy'] as [ShipClassId, Faction]] : [])]
      : kind === 'squadron' ? [['warship', 'navy'], ['frigate', 'navy']]
      : [['frigate', 'pirate']];
    // Their route: a loop that passes somewhere near where the player is now,
    // or (convoys) a passage straight across the area.
    const route: { x: number; z: number }[] = [];
    if (kind === 'convoy') {
      const b = a + Math.PI + this.rng.range(-0.6, 0.6);
      route.push({ x: p.x + this.rng.range(-1500, 1500), z: p.z + this.rng.range(-1500, 1500) });
      route.push({ x: p.x + Math.cos(b) * 14000, z: p.z + Math.sin(b) * 14000 });
    } else {
      const cx = p.x + this.rng.range(-2000, 2000), cz = p.z + this.rng.range(-2000, 2000);
      const r = this.rng.range(2500, 4500);
      const a0 = Math.atan2(z - cz, x - cx);
      for (let k = 1; k <= 6; k++) route.push({ x: cx + Math.cos(a0 + (k * TAU) / 6) * r, z: cz + Math.sin(a0 + (k * TAU) / 6) * r });
    }
    const first = route[0]!;
    const heading = Math.atan2(first.x - x, -(first.z - z));
    const out: Vessel[] = [];
    comp.forEach(([cls, faction], i) => {
      // Sail in company: a cable or two apart.
      const ox = x + Math.cos(a + 1.6) * i * 260, oz = z + Math.sin(a + 1.6) * i * 260;
      const v = new Vessel(SHIP_CLASSES[cls], faction, this.name(faction), ox, oz, heading, this.rng.int(1 << 30), this.difficulty, 3);
      v.captain = new Captain(v, this.difficulty, this.rng.int(1 << 30));
      v.captain.route = route.map((w) => ({ x: w.x + (ox - x), z: w.z + (oz - z) }));
      const h = heading;
      v.ship.vel.set(Math.sin(h) * 3, 0, -Math.cos(h) * 3);
      out.push(v);
    });
    // A convoy and its escort keep together: the escort guards the merchantmen.
    if (kind === 'convoy') {
      const ids = out.map((v) => v.id);
      for (const v of out) {
        v.captain!.convoy = ids;
        v.captain!.escort = v.faction !== 'merchant';
      }
    }
    this.groups.push({ kind, vessels: out });
    return out;
  }
}
